import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import apiServices from "../../../../services/apiService";
import MetricCard from "../../../common/Card/MetricCard";
import Chart from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faClock, faWrench, faBullhorn, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import ActionCenter from "../../../common/ActionCenter/ActionCenter";
import storageService from "../../../../utils/storageService";
import SiteAdminDashboard from "./SiteAdminDashboard";
import SuperAdminDashboard from "./SuperAdminDashboard";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const Overview = () => {
    const { user: authUser } = useSelector((state) => state.auth);
    console.log("profileData", authUser);

    const roleId = Number(authUser?.roleId || authUser?.RoleId || storageService.getItem("roleId"));
    const unitId = authUser?.branchorunitID;

    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
    useEffect(() => {
        const handleThemeChange = () => {
            setTheme(localStorage.getItem("theme") || "light");
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);
    const isDark = theme === "dark";

    const [usersData, setUsersData] = useState([]);
    const [showAllTeamMembers, setShowAllTeamMembers] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        siteContext: null,
        overview: [],
        visitorStats: [],
        totalVisitorCounts: { totalInviteVisitors: 0, totalWalkinVisitors: 0 }
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                if (!unitId) return;

                const response = await apiServices({
                    method: "GET",
                    url: `/dashboard?unitId=${unitId}`,
                    useNodeApi: true
                });

                if (response?.data?.success) {
                    setDashboardData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching unit admin dashboard data:", error);
            }
        };

        const fetchTeamMembers = async () => {
            try {
                const response = await apiServices({
                    method: "GET",
                    url: "/users",
                    useNodeApi: true
                });
                const users = response?.data?.data || [];
                setUsersData(users);
            } catch (error) {
                console.error("Error fetching team members:", error);
            }
        };

        fetchDashboardData();
        fetchTeamMembers();
    }, [unitId]);

    // ----------------------------------------------------
    // METRICS CALCULATION
    // ----------------------------------------------------
    const { overview, visitorStats, siteContext } = dashboardData;

    const totalUsers = overview.length > 0 ? overview[0].totalUsers : 0;
    const unitLabel = overview.length > 0 ? overview[0].label : "Unit";

    const today = new Date().toISOString().split('T')[0];
    const todaysVisitorStat = visitorStats.find(v => {
        const vDate = new Date(v.visitDate).toISOString().split('T')[0];
        return vDate === today;
    });
    const todaysVisitors = todaysVisitorStat ? (todaysVisitorStat.inviteVisitorCount + todaysVisitorStat.walkinVisitorCount) : 0;

    const topMetrics = [
        {
            title: "Total User",
            value: totalUsers,
            icon: faUsers,
            color: "text-red-500"
        },
        {
            title: "Today's visitors",
            value: todaysVisitors,
            icon: faUsers,
            color: "text-blue-500"
        }
    ];

    // ----------------------------------------------------
    // CHARTS CONFIGURATION
    // ----------------------------------------------------
    const visitorCategories = visitorStats.map(item => item.dayOfWeek.substring(0, 3));
    const inviteVisitorData = visitorStats.map(item => item.inviteVisitorCount);
    const walkinVisitorData = visitorStats.map(item => item.walkinVisitorCount);

    // 1. Visitor Activity (Line Chart)
    const lineChartOptions = {
        chart: { type: "line", toolbar: { show: false } },
        stroke: { curve: 'smooth', width: [3, 3] },
        colors: [primaryColor(), secondaryColor()],
        xaxis: { 
            categories: visitorCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: { 
            position: "top", 
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        markers: { size: 4 },
        dataLabels: { enabled: false }
    };

    const lineChartSeries = [
        { name: "Invited Visitors", data: inviteVisitorData },
        { name: "Walk-in Visitors", data: walkinVisitorData }
    ];

    // 2. Weekly Visitor Flow (Stacked Column Chart)
    const columnChartOptions = {
        chart: { type: "bar", stacked: true, toolbar: { show: false } },
        colors: [primaryColor(), secondaryColor()],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '35%',
                borderRadius: 4
            }
        },
        xaxis: { 
            categories: visitorCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: { 
            position: "top", 
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        fill: { opacity: 1 },
        dataLabels: { enabled: false }
    };

    const columnChartSeries = [
        { name: "Invited Visitors", data: inviteVisitorData },
        { name: "Walk-in Visitors", data: walkinVisitorData }
    ];

    // ----------------------------------------------------
    // QUICK ACCESS ACTIONS
    // ----------------------------------------------------
    const dashboardActions = [
        {
            icon: faUserPlus,
            title: "Invite Visitor",
            description: "Pre-register a new visitor",
            url: "/zentcomm/visitor-managment/invited-visitor",
            iconStyles: { bg: "bg-indigo-100", color: "text-indigo-600" }
        },
        {
            icon: faClock,
            title: "Attendance Logs",
            description: "Check and monitor user attendance",
            url: "/zentcomm/attendance",
            iconStyles: { bg: "bg-blue-100", color: "text-blue-600" }
        },
        {
            icon: faWrench,
            title: "Maintenance",
            description: "Manage maintenance requests and invoices",
            url: "/zentcomm/maintenance",
            iconStyles: { bg: "bg-green-100", color: "text-green-600" }
        },
        {
            icon: faBullhorn,
            title: "Announcements",
            description: "Post and view board announcements",
            url: "/zentcomm/announcement",
            iconStyles: { bg: "bg-purple-100", color: "text-purple-600" }
        }
    ];

    const currentDate = new Date();
    const currentHour = currentDate.getHours();
    const greeting = currentHour >= 5 && currentHour < 12 ? "Good Morning" : currentHour >= 12 && currentHour < 17 ? "Good Afternoon" : currentHour >= 17 && currentHour < 21 ? "Good Evening" : "Good Night";
    const formattedDate = currentDate.toLocaleString("en-US", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });

    if (roleId === 2) {
        return <SuperAdminDashboard />;
    }

    if (roleId === 3) {
        return <SiteAdminDashboard />;
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-dashboardbg">
            <div className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
                <div className="max-w-[1600px] mx-auto w-full">

                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-textprimary mb-1">
                            {greeting}, {authUser?.name || "-"}! 👋
                        </h1>
                        <p className="text-sm text-textsecondary">
                            {formattedDate} • Here is your unit overview
                        </p>
                    </div>

                    {/* Top Metric Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {topMetrics.map((metric, idx) => (
                            <MetricCard key={idx} {...metric} />
                        ))}
                    </div>

                    {/* Enterprise Plan (Site Context) */}
                    {siteContext && (
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 mb-6 flex flex-col md:flex-row items-center justify-between text-sm">
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-xl flex flex-col items-center justify-center font-bold text-xs">
                                    Logo
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-textprimary flex items-center gap-2">
                                        {siteContext.siteName} <span className="bg-indigo-50 text-indigo-600 text-[10px] px-2 py-0.5 rounded-full font-semibold">{unitLabel}</span>
                                    </h2>
                                    <div className="flex gap-8 mt-3 text-xs">
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Location</p>
                                            <p className="font-semibold text-textprimary">{siteContext.cityName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Primary Contact</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminFirstName} {siteContext.adminLastName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Phone</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminContact}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Email</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminEmail}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Row 2: Visitor Activity & Team Members */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Visitor Activity Line Chart */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-base font-bold text-textprimary">Visitor activity</h3>
                                <span className="bg-red-50 text-red-500 text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div> Live
                                </span>
                            </div>
                            <p className="text-xs text-textsecondary mb-4">This week — {unitLabel}</p>
                            <div className="h-[300px]">
                                <Chart options={lineChartOptions} series={lineChartSeries} type="line" height="100%" />
                            </div>
                        </div>

                        {/* Quick Access */}
                        <ActionCenter
                            title="Quick Access"
                            description="Quick shortcuts to key management workflows."
                            actions={dashboardActions}
                        />
                    </div>

                    {/* Row 3: Visitor Flow & Quick Access */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Visitor Flow Column Chart */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-base font-bold text-textprimary mb-1">Weekly visitor flow</h3>
                            <p className="text-xs text-textsecondary mb-4">{unitLabel} • Mon-Sun</p>
                            <div className="h-[300px]">
                                <Chart options={columnChartOptions} series={columnChartSeries} type="bar" height="100%" />
                            </div>
                        </div>

                        {/* Team Members */}
                        <div className={`bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 ${showAllTeamMembers ? 'overflow-hidden flex flex-col h-[400px]' : 'h-full'}`}>
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-base font-bold text-textprimary">Team members</h3>
                                <span className="bg-green-50 text-green-600 text-[10px] px-2 py-1 rounded font-semibold">ACTIVE</span>
                            </div>
                            <p className="text-[10px] text-textsecondary mb-4">{usersData.length} total users</p>

                            <div className={`${showAllTeamMembers ? 'flex-1 overflow-y-auto custom-scrollbar pr-2' : ''}`}>
                                <div className="space-y-4">
                                    {(showAllTeamMembers ? usersData : usersData.slice(0, 4)).map((user, idx) => {
                                        const colors = [
                                            "bg-orange-100 text-orange-600",
                                            "bg-blue-100 text-blue-600",
                                            "bg-green-100 text-green-600",
                                            "bg-purple-100 text-purple-600"
                                        ];
                                        const dotColors = ["bg-green-500", "bg-green-500", "bg-green-500", "bg-orange-400"];

                                        return (
                                            <div key={idx} className="flex items-center gap-3 py-1">
                                                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${colors[idx % 4]}`}>
                                                    {user.firstName?.charAt(0) || ''}{user.lastName?.charAt(0) || ''}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold text-textprimary truncate">{user.firstName} {user.lastName}</p>
                                                    <p className="text-[10px] text-textsecondary truncate">{user.roleName || 'User'}</p>
                                                </div>
                                                <div className={`w-1.5 h-1.5 rounded-full ${dotColors[idx % 4]} shrink-0 mr-1`}></div>
                                            </div>
                                        );
                                    })}

                                    {/* Always show View More section for design matching when collapsed */}
                                    {!showAllTeamMembers && (
                                        <div
                                            className="flex items-center gap-3 py-1 mt-1 cursor-pointer hover:bg-appbg rounded-lg p-1 -mx-1 transition-colors"
                                            onClick={() => setShowAllTeamMembers(true)}
                                        >
                                            <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-[#262626] text-gray-600 dark:text-gray-300 flex items-center justify-center font-bold text-xs shrink-0">
                                                +{usersData.length > 4 ? usersData.length - 4 : 20}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-textprimary truncate">
                                                    {usersData.length > 4 ? usersData.length - 4 : 20} more members
                                                </p>
                                                <p className="text-[10px] text-textsecondary truncate">View all</p>
                                            </div>
                                            <div className="text-xs font-bold text-red-500 mr-1">View</div>
                                        </div>
                                    )}

                                    {/* Show View Less when expanded */}
                                    {showAllTeamMembers && (
                                        <div
                                            className="flex items-center justify-center py-2 mt-2 cursor-pointer hover:bg-appbg rounded-lg transition-colors text-xs font-bold text-textsecondary"
                                            onClick={() => setShowAllTeamMembers(false)}
                                        >
                                            View less
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Overview;